// Right-hand main column. Splits the channel content area from the
// optional thread panel that slides in when `activeThreadRootId` is
// set. The ChannelColumn extends shared-gov's `AppMain` (header + body
// shell) and adds a Composer footer + filter bar specific to chat.
import { channelDisplayName } from '../../functions/chat/chatUtils.js'

export const ChatMain = {
  flow: 'x',
  flex: '1 1 0',
  minWidth: '0',
  minHeight: '0',
  overflow: 'hidden',
  theme: 'document',

  ChannelColumn: {
    extends: 'AppMain',
    padding: '0',

    // Header now hosts the tab switcher in its center group (Calendar's
    // ViewSwitcher pattern). The standalone Tabs row underneath has been
    // removed — see ChatHeader.CenterGroup below.
    Header: { extends: 'ChatHeader' },
    Body: null,
    ChatFilterBar: {},

    // The four tab views are stacked siblings — each uses `hide` to show
    // only when its tab is active. flex:1 + min-height:0 means whichever
    // one is visible expands to fill the remaining space and scrolls
    // independently.
    Conversation: {
      extends: 'ChatMessageList',
      width: '100%',
      maxWidth: 'I1',
      margin: '0 auto'
    },
    ChatFilesView: {},
    ChatLinksView: {},
    ChatTicketsView: {},

    // Composer only on the conversation tab.
    Footer: {
      extends: 'ChatComposer',
      hide: (el, s) => !(s && s.root && s.root.activeTab === 'conversation'),
      Toolbar: { width: '100%', maxWidth: 'I1', margin: '0 auto' },
      Box: { width: '100%', maxWidth: 'I1', margin: '0 auto' }
    }
  },

  Thread: {
    extends: 'ChatThreadPanel',
    hide: (el, s) => !(s && s.root && s.root.activeThreadRootId)
  },

  // Single shared hover-actions overlay — viewport-fixed, lives outside
  // the message-list and thread-panel scroll containers. Both lists use
  // setupHoverActions(scrollNode) to position this on bubble hover.
  HoverActions: { extends: 'ChatHoverActions' }
}

export const ChatHeader = {
  extends: 'PageHeader',
  padding: 'A B1',
  minHeight: 'D1',
  backdropFilter: 'blur(20px)',

  LeftGroup: {
    flow: 'y',
    align: 'flex-start left',
    gap: 'V',
    flex: '1',
    minWidth: '0',

    Title: {
      tag: 'h2',
      fontSize: 'B1',
      fontWeight: '600',
      letterSpacing: '-.01em',
      margin: '0',
      color: 'title',
      cursor: 'pointer',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ':hover': { opacity: '.8' },
      onClick: (e, el, s) => {
        const root = s && s.root
        if (!root) return
        const ch = (root.channels || []).find(
          (c) => c.id === root.activeChannelId
        )
        if (!ch) return
        root.update({ showChannelSettings: ch.id })
      },
      text: (el, s) => {
        const root = s && s.root
        if (!root) return 'Select a channel'
        const activeId = root.activeChannelId
        if (!activeId) return 'Select a channel'
        const ch = (root.channels || []).find((c) => c.id === activeId)
        if (ch) {
          const prefix = ch.kind === 'channel' ? '# ' : ''
          return (
            prefix +
            channelDisplayName(
              ch,
              root.channelMembers || [],
              el.call('peopleByEmail'),
              root.currentUserEmail,
              { firstOnly: ch.kind === 'group_dm' }
            )
          )
        }
        // Channel id is set (URL hash, deep link, cross-workspace handoff)
        // but the channel isn't in this user's loaded list yet. Show a
        // neutral placeholder instead of the misleading "Select a channel"
        // — the conversation pane is already rendering messages from it.
        return 'Channel'
      }
    },

    Topic: null,

    // Cross-resource link pills — surfaces the event/meet this channel is
    // tied to (if any). Clicking a pill navigates to that resource.
    Links: {
      extends: 'LinkedResources',
      marginTop: 'Y',
      type: 'chat_channel',
      id: (el, s) => s?.root?.activeChannelId || null
    }
  },

  // Center-positioned tab switcher — Conversation / Files / Links / Tickets.
  // Uses the shared `SharedViewSwitcher` from shared-gov, so the same
  // component drives calendar's view tabs and chat's content tabs.
  CenterGroup: {
    TabSwitcher: {
      extends: 'SharedViewSwitcher',
      stateKey: 'activeTab',
      childExtends: 'SharedViewSwitcherBtn',
      children: [
        { value: 'conversation', label: 'Conversation' },
        { value: 'files', label: 'Files' },
        { value: 'links', label: 'Links' },
        { value: 'tickets', label: 'Tickets' }
      ]
    }
  },

  RightGroup: {
    gap: 'A',

    // Cross-app: start a meet call with all members of the active channel /
    // DM. Creates (or reuses) a meet_rooms row linked via chat_channel_id,
    // auto-seeds meet_room_members from chat_channel_members, and drops the
    // initiator straight into the call. Other members see the room appear
    // in their /meet list with a LIVE badge (via realtime).
    CallChannelBtn: {
      extends: 'IconButton',
      hide: (el, s) => !s?.root?.activeChannelId,
      Icon: { icon: 'video' },
      title: 'Call all participants',
      aria: { label: 'Call all participants' },
      onClick: async (e, el, s) => {
        const root = s.root
        const channelId = root.activeChannelId
        if (!channelId) return
        const channel = (root.channels || []).find((c) => c.id === channelId)
        if (!channel) return
        const isDm = channel.kind === 'dm' || channel.kind === 'group_dm'
        // Reuse an existing active meet room for this channel if one exists.
        const existing = (root.rooms || []).find(
          (r) => r.chatChannelId === channelId && !r.endedAt
        )
        let id = existing?.id
        if (!id) {
          id = await el.call('createRoom', {
            name:
              (channel.name || (isDm ? 'Direct call' : 'Channel call')) +
              (isDm ? '' : ' · call'),
            kind: isDm ? 'dm' : 'room',
            chatChannelId: channelId,
            // DMs default transcribe-off (privacy); channels default on.
            transcribeEnabled: !isDm,
            // Private unless it's an already-public channel.
            isPrivate: isDm || !!channel.is_private
          })
        }
        if (!id) return
        root.update({ activeRoomId: id, meetView: 'call' })
        if (typeof window !== 'undefined') {
          window.location.hash = `room=${id}&view=call`
        }
        await el.call('connectCall', id)
        let __root = el
        while (__root && !__root.routes) __root = __root.parent
        if (__root) el.router('/meet', __root)
      }
    },

    // AiToggle / Profile already live in the global AppHeader.
    // Chat-only actions: filter toggle and member count.
    FilterBtn: {
      extends: 'IconButton',
      Icon: { icon: 'filter' },
      title: 'Toggle filters',
      aria: { label: 'Toggle filters' },
      data: { action: 'toggle-filter-bar' }
    },


    MemberCount: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'caption',
      text: (el, s) => {
        const root = s && s.root
        if (!root || !root.activeChannelId) return ''
        const n = (root.channelMembers || []).filter(
          (m) => m.channel_id === root.activeChannelId
        ).length
        return `${n} member${n === 1 ? '' : 's'}`
      }
    }
  }
}
